import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { FiCalendar, FiCheck, FiChevronRight, FiXCircle } from 'react-icons/fi';
import firebaseFirestore from '~/config/firebase/firestore';

/**
 * Las reservas de una serie, para quien llega desde el correo.
 *
 * De una reserva que se repite sale un solo correo, con el código de la primera.
 * Al abrir ese enlace aparece aquí la lista entera: cada fecha con su hora, su
 * sala y su propio código, y desde cualquiera se puede entrar a cambiarla o
 * cancelarla sin tocar las demás.
 *
 * No hace falta ninguna comprobación extra de identidad: el código de la URL ya
 * es la llave, igual que en el correo de una reserva suelta.
 */

type Props = {
  /** Id de la serie, tal como se guardó en `recurrence.id`. */
  recurrenceId: string;
  /** Código que se está viendo ahora mismo, para resaltarlo. */
  codigoActual?: string;
  /** Abre otra reserva de la serie en la misma pantalla. */
  onAbrir: (code: string) => void;
};

type Fila = {
  id: string;
  code: string;
  fecha: Date | null;
  fechaTexto: string;
  horaInicio: string;
  horaFin: string;
  sala: string;
  cancelada: boolean;
  pasada: boolean;
};

const MisReservasSerie = ({ recurrenceId, codigoActual, onAbrir }: Props) => {
  const [filas, setFilas] = useState<Fila[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!recurrenceId) return;
    let vivo = true;
    (async () => {
      setCargando(true);
      try {
        const snap = await getDocs(
          query(
            collection(firebaseFirestore, 'reservations'),
            where('recurrence.id', '==', recurrenceId)
          )
        );
        if (!vivo) return;

        const ahora = Date.now();
        const lista: Fila[] = snap.docs.map((d) => {
          const r = d.data() as any;
          const cuando = r.startDatetime?.toDate?.() ?? r.datetime?.toDate?.() ?? null;
          return {
            id: d.id,
            code: r.code ?? '',
            fecha: cuando,
            fechaTexto: cuando
              ? format(cuando, "EEEE d 'de' MMMM", { locale: esLocale })
              : r.date ?? '',
            horaInicio: r.startHour ?? '',
            horaFin: r.endHour && r.endHour !== '--' ? r.endHour : '',
            sala: r.location?.name ?? '',
            cancelada: r.status === 'cancelled',
            pasada: cuando ? cuando.getTime() < ahora : false,
          };
        });

        lista.sort((a, b) => (a.fecha?.getTime() ?? 0) - (b.fecha?.getTime() ?? 0));
        setFilas(lista);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [recurrenceId]);

  const activas = useMemo(() => filas.filter((f) => !f.cancelada && !f.pasada), [filas]);

  if (cargando) {
    return (
      <Box w='full' maxW='620px' mb={6}>
        <Skeleton h='26px' mb={3} borderRadius='md' />
        <VStack align='stretch' spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} h='58px' borderRadius='md' />
          ))}
        </VStack>
      </Box>
    );
  }

  if (filas.length <= 1) return null;

  return (
    <Box w='full' maxW='620px' mb={8}>
      <HStack spacing={2} mb={1}>
        <Icon as={FiCalendar} color='blue.500' />
        <Text fontWeight='bold' fontSize={{ base: 'md', md: 'lg' }}>
          Tus {filas.length} reservas
        </Text>
      </HStack>
      <Text fontSize='sm' color='gray.600' mb={4}>
        {activas.length === 0
          ? 'Ya no queda ninguna por venir.'
          : `Quedan ${activas.length} por venir. Toca una para cambiarla o cancelarla; las demás no se tocan.`}
      </Text>

      <VStack align='stretch' spacing={2}>
        {filas.map((fila) => {
          const esActual = Boolean(codigoActual) && fila.code === codigoActual;
          const apagada = fila.cancelada || fila.pasada;

          return (
            <Flex
              key={fila.id}
              onClick={apagada ? undefined : () => onAbrir(fila.code)}
              role={apagada ? undefined : 'button'}
              tabIndex={apagada ? undefined : 0}
              onKeyDown={
                apagada
                  ? undefined
                  : (e) => {
                      if (e.key === 'Enter' || e.key === ' ') onAbrir(fila.code);
                    }
              }
              textAlign='left'
              align='center'
              gap={3}
              w='full'
              px={3}
              py={3}
              borderWidth={esActual ? '2px' : '1px'}
              borderColor={esActual ? 'blue.400' : 'gray.200'}
              borderRadius='lg'
              bg={esActual ? 'blue.50' : apagada ? 'gray.50' : 'white'}
              opacity={fila.cancelada ? 0.6 : 1}
              cursor={apagada ? 'default' : 'pointer'}
              _hover={apagada ? undefined : { borderColor: 'blue.300', bg: 'blue.50' }}
              transition='all .15s'
            >
              <Icon
                as={fila.cancelada ? FiXCircle : FiCheck}
                color={fila.cancelada ? 'red.400' : fila.pasada ? 'gray.400' : 'green.500'}
                boxSize={4}
                flexShrink={0}
              />

              <Box flex={1} minW={0}>
                <Text
                  fontSize='sm'
                  fontWeight='semibold'
                  noOfLines={1}
                  textDecoration={fila.cancelada ? 'line-through' : undefined}
                  sx={{ '&::first-letter': { textTransform: 'uppercase' } }}
                >
                  {fila.fechaTexto}
                </Text>
                <Text fontSize='xs' color='gray.600' noOfLines={1}>
                  {fila.horaInicio}
                  {fila.horaFin ? `–${fila.horaFin}` : ''} · {fila.sala}
                </Text>
              </Box>

              <VStack spacing={0.5} align='flex-end' flexShrink={0}>
                {fila.cancelada ? (
                  <Badge colorScheme='red' fontSize='0.6em'>
                    Cancelada
                  </Badge>
                ) : fila.pasada ? (
                  <Badge colorScheme='gray' fontSize='0.6em'>
                    Ya pasó
                  </Badge>
                ) : (
                  <Badge colorScheme={esActual ? 'blue' : 'gray'} fontSize='0.6em'>
                    {fila.code}
                  </Badge>
                )}
              </VStack>

              {!apagada && (
                <Icon as={FiChevronRight} color='gray.400' boxSize={4} flexShrink={0} />
              )}
            </Flex>
          );
        })}
      </VStack>

      {codigoActual && (
        <Text fontSize='xs' color='gray.500' mt={3}>
          Abajo estás viendo la reserva {codigoActual}.
        </Text>
      )}
    </Box>
  );
};

export default MisReservasSerie;
